import {
    type CellContext,
    type CellData,
    type Column,
    type ColumnDef,
    type ColumnHelper,
    columnFilteringFeature,
    columnVisibilityFeature,
    createColumnHelper,
    createExpandedRowModel,
    createFilteredRowModel,
    createPaginatedRowModel,
    createSortedRowModel,
    filterFns,
    globalFilteringFeature,
    type ReactTable,
    type Row,
    type RowData,
    rowExpandingFeature,
    rowPaginationFeature,
    rowSortingFeature,
    sortFns,
    tableFeatures,
} from '@tanstack/react-table';

/**
 * Everything a DataTable can do, registered once. The built-in sort and filter registries are
 * whole, so `'auto'` picks the same function for a column's values as it always has.
 */
export const dataTableFeatures = tableFeatures({
    columnFilteringFeature,
    globalFilteringFeature,
    rowSortingFeature,
    rowExpandingFeature,
    rowPaginationFeature,
    columnVisibilityFeature,
    filteredRowModel: createFilteredRowModel(),
    sortedRowModel: createSortedRowModel(),
    expandedRowModel: createExpandedRowModel(),
    paginatedRowModel: createPaginatedRowModel(),
    filterFns,
    sortFns,
});

export type DataTableFeatures = typeof dataTableFeatures;

/** A column of a DataTable over `TData` rows. */
export type DataTableColumnDef<
    TData extends RowData,
    TValue extends CellData = CellData,
> = ColumnDef<DataTableFeatures, TData, TValue>;
export type DataTableColumn<TData extends RowData> = Column<DataTableFeatures, TData>;
export type DataTableRow<TData extends RowData> = Row<DataTableFeatures, TData>;
export type DataTableCellContext<
    TData extends RowData,
    TValue extends CellData = CellData,
> = CellContext<DataTableFeatures, TData, TValue>;
export type DataTableInstance<TData extends RowData> = ReactTable<DataTableFeatures, TData>;

/** Column helpers typed for a DataTable over `TData` rows. */
export function createDataTableColumnHelper<TData extends RowData>(): ColumnHelper<
    DataTableFeatures,
    TData
> {
    return createColumnHelper<DataTableFeatures, TData>();
}
